import { useState } from "react";
import { SectionCard } from "./SectionCard";
import { api, fmtUSD } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const empty = { tipo: "Hipoteca 1er rango", cliente: "", descripcion: "", valor_estimado_usd: "", estado: "En proceso", gravamenes: "" };
const TIPOS = ["Pagaré a sola firma", "Pagaré con codeudoría", "Pagarés de terceros", "Hipoteca 1er rango", "Hipoteca 2do rango", "Hipoteca 3er rango",
  "Fideicomiso", "Prenda de maquinaria", "Prenda de granos", "Otra"];
const CON_GRAVAMEN = ["Hipoteca 2do rango", "Hipoteca 3er rango"];
const ESTADOS = ["En proceso", "Aprobada", "Ejecutada", "Vencida"];

const estadoStyle = {
  "En proceso": "bg-blue-100 text-blue-700 border-blue-200",
  Aprobada: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Ejecutada: "bg-slate-200 text-slate-700 border-slate-300",
  Vencida: "bg-red-100 text-red-700 border-red-200",
};

export default function Garantias({ items, reload, canEdit }) {
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startEdit = (g) => {
    setEditing(g.id);
    setForm({ tipo: g.tipo, cliente: g.cliente, descripcion: g.descripcion, valor_estimado_usd: g.valor_estimado_usd, estado: g.estado, gravamenes: g.gravamenes || "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, valor_estimado_usd: parseFloat(form.valor_estimado_usd) || 0, gravamenes: CON_GRAVAMEN.includes(form.tipo) ? (parseFloat(form.gravamenes) || 0) : 0 };
      if (editing) await api.put(`/garantias/${editing}`, payload);
      else await api.post("/garantias", payload);
      toast.success(editing ? "Garantía actualizada" : "Garantía registrada");
      setForm(empty);
      setEditing(null);
      setOpen(false);
      reload();
    } catch (err) {
      toast.error("No se pudo guardar la garantía");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/garantias/${id}`);
    toast.success("Garantía eliminada");
    reload();
  };

  return (
    <SectionCard
      id="garantias"
      testid="section-garantias"
      num="6"
      title="Garantías en Curso de los Clientes"
      description="Cargue, actualice y visualice las garantías en curso de los clientes."
      action={
        canEdit && (
        <Button
          data-testid="open-garantia-form"
          onClick={() => { setEditing(null); setForm(empty); setOpen(!open); }}
          className="bg-emerald-700 hover:bg-emerald-800 h-9"
        >
          {open ? <X className="h-4 w-4 mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
          {open ? "Cerrar" : "Nueva Garantía"}
        </Button>
        )
      }
    >
      {open && canEdit && (
        <form onSubmit={submit} className="mb-5 p-4 rounded-lg border border-emerald-100 bg-emerald-50/50 grid sm:grid-cols-2 gap-3 animate-fade-up">
          <div className="space-y-1.5">
            <Label>Tipo de Garantía</Label>
            <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
              <SelectTrigger data-testid="garantia-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>{(TIPOS.includes(form.tipo) ? TIPOS : [form.tipo, ...TIPOS]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Cliente</Label>
            <Input data-testid="garantia-cliente" value={form.cliente} onChange={set("cliente")} required />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Descripción del bien / documento respaldatorio</Label>
            <Textarea data-testid="garantia-descripcion" value={form.descripcion} onChange={set("descripcion")} rows={2} required />
          </div>
          <div className="space-y-1.5">
            <Label>Valor Estimado (USD)</Label>
            <Input type="number" step="0.01" data-testid="garantia-valor" value={form.valor_estimado_usd} onChange={set("valor_estimado_usd")} required />
          </div>
          {CON_GRAVAMEN.includes(form.tipo) && (
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Deudas anteriores sobre el inmueble (USD) — lo que cobran otros acreedores antes que nosotros</Label>
              <Input type="number" step="0.01" data-testid="garantia-gravamenes" value={form.gravamenes} onChange={set("gravamenes")} placeholder="Ej.: hipoteca de 1er rango de un banco" />
              <p className="text-[11px] text-slate-500">En el campo «Valor Estimado» poné el valor del inmueble; acá, el total de los gravámenes que ya tiene.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Estado Actual</Label>
            <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
              <SelectTrigger data-testid="garantia-estado"><SelectValue /></SelectTrigger>
              <SelectContent>{ESTADOS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" data-testid="add-garantia-submit-button" disabled={saving} className="bg-emerald-700 hover:bg-emerald-800">
              {editing ? "Actualizar Garantía" : "Guardar Garantía"}
            </Button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3 font-semibold">Tipo</th>
              <th className="text-left px-4 py-3 font-semibold">Cliente</th>
              <th className="text-left px-4 py-3 font-semibold">Descripción</th>
              <th className="text-right px-4 py-3 font-semibold">Valor Estimado</th>
              <th className="text-center px-4 py-3 font-semibold">Estado</th>
              {canEdit && <th className="text-right px-4 py-3 font-semibold">Acc.</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Sin garantías registradas.</td></tr>
            )}
            {items.map((g) => (
              <tr key={g.id} data-testid={`garantia-row-${g.id}`} className="border-t border-slate-100 hover:bg-emerald-50/40 align-top">
                <td className="px-4 py-2.5 font-medium text-slate-800">
                  <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />{g.tipo}</span>
                </td>
                <td className="px-4 py-2.5 text-slate-700">{g.cliente}</td>
                <td className="px-4 py-2.5 text-slate-600 max-w-[260px]">{g.descripcion}</td>
                <td className="px-4 py-2.5 text-right font-mono font-semibold text-emerald-700">{fmtUSD(g.valor_estimado_usd)}{g.gravamenes ? <div className="text-[10px] font-normal text-slate-500">con deudas anteriores por {fmtUSD(g.gravamenes)}</div> : null}</td>
                <td className="px-4 py-2.5 text-center">
                  <span className={`px-2.5 py-1 text-[10px] font-semibold border rounded-full ${estadoStyle[g.estado] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                    {g.estado}
                  </span>
                </td>
                {canEdit && (
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(g)} data-testid={`edit-garantia-${g.id}`} className="p-1.5 text-slate-400 hover:text-emerald-700"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(g.id)} data-testid={`delete-garantia-${g.id}`} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}
